import React from 'react';
import { useGoogleLogin } from '@react-oauth/google';

interface GoogleLoginButtonProps {
  onSuccess: (accessToken: string) => void;
  onError?: (error: Error) => void;
  className?: string;
  disabled?: boolean;
}

// Google icon SVG
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onSuccess,
  onError,
  className = '',
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = React.useState(false);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(false);
      // Get ID token by exchanging access token
      // For simplicity, we'll use the access token to get user info
      try {
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        
        if (!userInfoResponse.ok) {
          throw new Error('Failed to get user info');
        }
        
        // Pass the access token to the parent
        onSuccess(tokenResponse.access_token);
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Google login failed'));
      }
    },
    onError: (error) => {
      setIsLoading(false);
      console.error('Google login error:', error);
      onError?.(new Error(error.error_description || 'Google login failed'));
    },
    onNonOAuthError: (error) => {
      setIsLoading(false);
      console.error('Google non-OAuth error:', error);
      onError?.(new Error('Google login was cancelled or failed'));
    },
  });

  const handleClick = () => {
    setIsLoading(true);
    login();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || isLoading}
      className={`flex items-center justify-center gap-2 w-full bg-white hover:bg-gray-100 text-gray-700 font-medium py-2.5 px-4 rounded border border-gray-300 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      style={{ minHeight: '40px' }}
    >
      <GoogleIcon />
      {isLoading ? 'Signing in...' : 'Continue with Google'}
    </button>
  );
};

export default GoogleLoginButton;

